import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ConnectorLibraryMedia,
  fetchLibraryMediaOpens,
  LibraryMediaCampaignRate,
  LibraryMediaOpen,
  LibraryMediaOpensSummary,
  LibraryMediaTopClient,
} from '../services/api';

const ACCENT = '#ed8936';
const ACCENT_BLUE = '#3056D3';
const ACCENT_BLUE_DARK = '#7EA6FF';
const OPENED_GREEN = '#059669';
const OPENED_GREEN_DARK = '#57D9A3';
const NOT_OPENED = '#64748B';
const NOT_OPENED_DARK = '#95A1B4';
const BAR_COLORS = ['#ed8936', '#dd6b20', '#c05621', '#9c4221', '#7b341e'];

function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function formatOpenRatePct(rate: number): string {
  const pct = rate * 100;
  if (pct >= 10 || pct === 0) return `${Math.round(pct)}%`;
  return `${pct.toFixed(1)}%`;
}

function formatOpenAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function truncateLabel(name: string, max = 22): string {
  return name.length > max ? `${name.slice(0, max - 2)}…` : name;
}

function useIsDarkTheme(): boolean {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined'
      && (document.documentElement.getAttribute('data-theme') === 'dark'
        || document.documentElement.classList.contains('dark')),
  );
  useEffect(() => {
    const sync = () => {
      setDark(
        document.documentElement.getAttribute('data-theme') === 'dark'
          || document.documentElement.classList.contains('dark'),
      );
    };
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/** Recharts SVG attrs often ignore CSS variables — resolve to concrete colors. */
function useChartThemeColors() {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [colors, setColors] = useState({
    text: '#e2e8f0',
    textMuted: '#a0aec0',
    border: '#4a5568',
    surface: '#1a202c',
    grid: 'rgba(148, 163, 184, 0.25)',
  });

  useEffect(() => {
    const resolve = () => {
      const host = rootRef.current;
      if (!host) return;
      const probe = document.createElement('span');
      probe.style.display = 'none';
      host.appendChild(probe);
      const read = (token: string, fallback: string) => {
        probe.style.color = '';
        probe.style.color = `var(${token})`;
        return getComputedStyle(probe).color || fallback;
      };
      const readBg = (token: string, fallback: string) => {
        probe.style.backgroundColor = '';
        probe.style.backgroundColor = `var(${token})`;
        return getComputedStyle(probe).backgroundColor || fallback;
      };
      setColors({
        text: read('--color-text-strong', '#e2e8f0'),
        textMuted: read('--color-text-subtle', '#a0aec0'),
        border: read('--color-border', '#4a5568'),
        surface: readBg('--color-surface', '#1a202c'),
        grid: read('--color-border', 'rgba(148, 163, 184, 0.25)'),
      });
      host.removeChild(probe);
    };
    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, []);

  return { rootRef, colors };
}

const sectionCardSx = {
  p: { xs: 2, md: 2.5 },
  bgcolor: 'var(--color-surface-elevated)',
  border: '1px solid rgba(var(--color-border-rgb), 0.16)',
  borderRadius: 2.5,
  borderLeft: `4px solid ${ACCENT}`,
} as const;

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={sectionCardSx}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', mb: subtitle ? 0.25 : 1.25 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', display: 'block', mb: 1.25 }}>
          {subtitle}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function KpiTile({
  label,
  accent,
  value,
  hint,
}: {
  label: string;
  accent: string;
  value: number;
  hint?: string;
}) {
  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: `1px solid ${hexToRgba(accent, 0.55)}`,
        borderLeft: `4px solid ${accent}`,
        background: `linear-gradient(150deg, ${hexToRgba(accent, 0.24)}, ${hexToRgba(accent, 0.07)} 70%)`,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: accent,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '1.85rem',
          fontWeight: 800,
          lineHeight: 1.1,
          color: 'var(--color-text-strong)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value.toLocaleString()}
      </Typography>
      {hint ? (
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function OpenRateDonut({
  openRate,
  uniqueOpeners,
  clientsSent,
  size = 118,
}: {
  openRate: number;
  uniqueOpeners: number;
  clientsSent: number;
  size?: number;
}) {
  const dark = useIsDarkTheme();
  const openedColor = dark ? OPENED_GREEN_DARK : OPENED_GREEN;
  const notOpenedColor = dark ? NOT_OPENED_DARK : NOT_OPENED;
  const strokeWidth = 12;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const openedFrac = clientsSent > 0 ? Math.min(Math.max(openRate, 0), 1) : 0;
  const notOpenedFrac = clientsSent > 0 ? 1 - openedFrac : 0;
  const gap = clientsSent > 0 ? Math.min(3, circumference / 60) : 0;
  const openedDash = Math.max(openedFrac * circumference - gap, openedFrac > 0 ? 0.5 : 0);
  const notOpenedDash = Math.max(notOpenedFrac * circumference - gap, notOpenedFrac > 0 ? 0.5 : 0);
  const pctLabel = clientsSent > 0 ? formatOpenRatePct(openRate) : '—';

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0, alignSelf: 'center' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(var(--color-border-rgb), 0.12)"
          strokeWidth={strokeWidth}
        />
        {openedFrac > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={openedColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${openedDash} ${circumference - openedDash}`}
            strokeDashoffset={0}
          />
        )}
        {notOpenedFrac > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={notOpenedColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${notOpenedDash} ${circumference - notOpenedDash}`}
            strokeDashoffset={-(openedFrac * circumference)}
          />
        )}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.35rem',
            lineHeight: 1,
            color: 'var(--color-text-strong)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {pctLabel}
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', fontWeight: 600 }}>
          Opened
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.45)', fontSize: '0.65rem' }}>
          {uniqueOpeners}/{clientsSent}
        </Typography>
      </Box>
    </Box>
  );
}

function CampaignOpenRateList({ rates }: { rates: LibraryMediaCampaignRate[] }) {
  const dark = useIsDarkTheme();
  const fill = dark ? OPENED_GREEN_DARK : OPENED_GREEN;
  const track = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rates.map((row, idx) => {
        const pct = Math.min(Math.max(row.openRate, 0), 1) * 100;
        return (
          <Box key={row.campaignId ?? `${row.campaignName}-${idx}`}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
              <Typography
                variant="body2"
                title={row.campaignName}
                sx={{
                  fontWeight: 700,
                  color: 'var(--color-text-strong)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {row.campaignName}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  color: fill,
                  flexShrink: 0,
                  fontSize: '0.95rem',
                }}
              >
                {formatOpenRatePct(row.openRate)}
              </Typography>
            </Box>
            <Box
              sx={{
                height: 10,
                borderRadius: 999,
                bgcolor: track,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${pct}%`,
                  height: '100%',
                  borderRadius: 999,
                  bgcolor: fill,
                  minWidth: pct > 0 ? 4 : 0,
                  transition: 'width 200ms ease',
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', mt: 0.35, display: 'block' }}>
              {row.uniqueOpeners} of {row.clientsSent} client{row.clientsSent === 1 ? '' : 's'} opened
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function HorizontalBarChartPanel({
  data,
  valueKey,
  nameKey,
  fullNameKey,
  xTickFormatter,
  domainMax,
  tooltipFormatter,
  barColors,
  showGrid = true,
  showEndLabels = false,
}: {
  data: Array<Record<string, string | number | null | undefined>>;
  valueKey: string;
  nameKey: string;
  fullNameKey: string;
  xTickFormatter?: (v: number) => string;
  domainMax: number;
  tooltipFormatter: (row: Record<string, string | number | null | undefined>) => string;
  barColors: string[];
  showGrid?: boolean;
  showEndLabels?: boolean;
}) {
  const { rootRef, colors } = useChartThemeColors();
  const chartHeight = Math.max(120, Math.min(260, 36 + data.length * 36));

  return (
    <Box ref={rootRef} sx={{ width: '100%', height: chartHeight }}>
      <ResponsiveContainer>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: showEndLabels ? 36 : 28, left: 4, bottom: 4 }}
        >
          {showGrid ? (
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
          ) : null}
          <XAxis
            type="number"
            domain={[0, domainMax]}
            tickFormatter={xTickFormatter}
            tick={showEndLabels ? false : { fontSize: 12, fill: colors.textMuted }}
            axisLine={false}
            tickLine={false}
            hide={showEndLabels}
          />
          <YAxis
            type="category"
            dataKey={nameKey}
            width={112}
            tick={{ fontSize: 12, fill: colors.text }}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip
            cursor={{ fill: hexToRgba(ACCENT, 0.08) }}
            contentStyle={{
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              fontSize: '0.75rem',
              color: colors.text,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            }}
            labelStyle={{ color: colors.text, fontWeight: 700 }}
            itemStyle={{ color: colors.textMuted }}
            formatter={(_value, _name, item) => {
              const row = item?.payload as Record<string, string | number | null | undefined> | undefined;
              if (!row) return ['—', ''];
              return [tooltipFormatter(row), ''];
            }}
            labelFormatter={(_, payload) => {
              const row = payload?.[0]?.payload as Record<string, string | number | null | undefined> | undefined;
              return String(row?.[fullNameKey] ?? '');
            }}
          />
          <Bar dataKey={valueKey} radius={[0, 6, 6, 0]} maxBarSize={42}>
            {data.map((d, i) => (
              <Cell key={String(d[fullNameKey] ?? i)} fill={barColors[i % barColors.length]} />
            ))}
            {showEndLabels ? (
              <LabelList
                dataKey={valueKey}
                position="right"
                fill={colors.text}
                fontSize={12}
                fontWeight={700}
              />
            ) : null}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

type Props = {
  open: boolean;
  media: ConnectorLibraryMedia | null;
  onClose: () => void;
};

export default function MediaOpensDialog({ open, media, onClose }: Props) {
  const dark = useIsDarkTheme();
  const clientsSentAccent = dark ? ACCENT_BLUE_DARK : ACCENT_BLUE;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opens, setOpens] = useState<LibraryMediaOpen[]>([]);
  const [campaignRates, setCampaignRates] = useState<LibraryMediaCampaignRate[]>([]);
  const [summary, setSummary] = useState<LibraryMediaOpensSummary | null>(null);
  const [topClients, setTopClients] = useState<LibraryMediaTopClient[]>([]);

  useEffect(() => {
    if (!open || !media) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOpens([]);
    setCampaignRates([]);
    setSummary(null);
    setTopClients([]);
    void (async () => {
      try {
        const result = await fetchLibraryMediaOpens(media.id);
        if (cancelled) return;
        setOpens(result.opens || []);
        setCampaignRates(result.campaignRates || []);
        setSummary(result.summary || {
          totalOpens: 0,
          uniqueOpeners: 0,
          clientsSent: 0,
          campaignsUsing: 0,
          openRate: 0,
        });
        setTopClients(result.topClients || []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load opens');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, media]);

  const hasAnalytics = !!summary && (
    summary.clientsSent > 0
    || summary.campaignsUsing > 0
    || opens.length > 0
    || campaignRates.length > 0
  );

  const clientChartData = topClients.slice(0, 5).map((c) => ({
    ...c,
    name: truncateLabel(c.clientName),
    fullName: c.clientName,
  }));
  const maxOpenCount = Math.max(1, ...clientChartData.map((d) => d.openCount), 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          backgroundImage: 'none',
          minHeight: { xs: 'auto', md: '70vh' },
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pr: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6">Media opens</Typography>
            <Typography
              variant="body2"
              title={media?.name}
              sx={{
                color: 'rgba(var(--color-text-rgb), 0.72)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {media?.name || '—'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !hasAnalytics ? (
          <Box
            sx={{
              py: 5,
              px: 2,
              textAlign: 'center',
              border: '1px dashed var(--color-border)',
              borderRadius: 2,
              bgcolor: 'var(--color-bg)',
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 36, color: 'var(--color-text-faint)', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
              No sends or opens yet. Attach this file to a campaign and send it to start tracking.
            </Typography>
          </Box>
        ) : (
          <>
            <SectionCard
              title="At a glance"
              subtitle="Open rate is unique clients who opened ÷ clients sent this media"
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto 1fr 1fr' },
                  gap: 1.5,
                  alignItems: 'stretch',
                }}
              >
                <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, display: 'flex', justifyContent: 'center' }}>
                  <OpenRateDonut
                    openRate={summary!.openRate}
                    uniqueOpeners={summary!.uniqueOpeners}
                    clientsSent={summary!.clientsSent}
                  />
                </Box>
                <KpiTile
                  label="Clients sent"
                  accent={clientsSentAccent}
                  value={summary!.clientsSent}
                  hint="Distinct recipients with a track link"
                />
                <KpiTile
                  label="Campaigns"
                  accent={ACCENT}
                  value={summary!.campaignsUsing}
                  hint="Campaigns that use this media"
                />
              </Box>
            </SectionCard>

            {campaignRates.length > 0 && (
              <SectionCard
                title="Top campaigns by open rate"
                subtitle="Unique openers ÷ clients sent per campaign (top 5)"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                  <CampaignOutlinedIcon sx={{ fontSize: 16, color: ACCENT }} />
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                    Higher is better · max 100%
                  </Typography>
                </Box>
                <CampaignOpenRateList rates={campaignRates} />
              </SectionCard>
            )}

            {clientChartData.length > 0 && (
              <SectionCard
                title="Top clients"
                subtitle="Most opens of this media (top 5)"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                  <PersonOutlineIcon sx={{ fontSize: 16, color: ACCENT }} />
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>
                    Counts every open, including reopens
                  </Typography>
                </Box>
                <HorizontalBarChartPanel
                  data={clientChartData}
                  valueKey="openCount"
                  nameKey="name"
                  fullNameKey="fullName"
                  domainMax={Math.max(1, Math.ceil(maxOpenCount * 1.15))}
                  barColors={BAR_COLORS}
                  showGrid={false}
                  showEndLabels
                  tooltipFormatter={(row) =>
                    `${row.openCount} open${Number(row.openCount) === 1 ? '' : 's'} · last ${formatOpenAt(String(row.lastViewedAt))}`}
                />
              </SectionCard>
            )}

            <SectionCard title="Activity">
              {opens.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <PeopleOutlineIcon sx={{ fontSize: 18, color: 'var(--color-text-faint)' }} />
                  <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)' }}>
                    Sent to {summary!.clientsSent} client{summary!.clientsSent === 1 ? '' : 's'}, but no opens yet.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 280,
                    overflow: 'auto',
                    border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                    borderRadius: 2,
                    bgcolor: 'var(--color-bg)',
                  }}
                >
                  {opens.map((row, idx) => (
                    <Box
                      key={`${row.clientId}-${row.viewedAt}-${idx}`}
                      sx={{
                        display: 'flex',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.15,
                        borderBottom: idx < opens.length - 1 ? '1px solid rgba(var(--color-border-rgb), 0.12)' : 'none',
                        '&:hover': { bgcolor: 'var(--color-surface)' },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: ACCENT,
                          mt: 0.7,
                          flexShrink: 0,
                          opacity: 0.85,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.35, color: 'var(--color-text-strong)' }}>
                          <Box component="span" fontWeight={700}>{row.clientName}</Box>
                          {' opened'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--color-text-subtle)', display: 'block', mt: 0.15 }}
                        >
                          {formatOpenAt(row.viewedAt)}
                          {row.campaignName ? ` · ${row.campaignName}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </SectionCard>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)', px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
